<template>
  <h1>Service Price Logs</h1>

  <div class="content-section filters">
    <div class="alert alert-warning" role="alert">
      {{ garmentTypeId ? `Logs for garment type ${garmentTypeId}.` : 'Logs for all garment types.' }}
      After setting filters or changing sorting order, click "Apply filter" to re-query the database.
    </div>

    <div class="row">
      <div class="col-md-3">
        <FormKit
            type="datepicker"
            label="Start Date"
            v-model="selectedStartDate"
            format="DD/MM/YYYY"
            timezone="Europe/London"
            clearable
            :key="datepickerClearedKey"
        ></FormKit>
      </div>

      <div class="col-md-3">
        <FormKit
            type="datepicker"
            label="End Date"
            v-model="selectedEndDate"
            format="DD/MM/YYYY"
            timezone="Europe/London"
            clearable
            :key="datepickerClearedKey"
        ></FormKit>
      </div>

      <div class="col-md-3">
        <label class="formkit-label" for="garment-type-filter">Garment Name</label>
        <input
            id="garment-type-filter"
            v-model="garmentTypeFilter"
            class="form-control"
            type="text"
            placeholder="Filter by garment name"
            @keydown.enter.prevent="applyFilter(true)"
        />
      </div>
    </div>

    <div class="action-bar">
      <button @click="clearFilter" id="clearFilter">Clear filter</button>&#160;
      <button @click="applyFilter(true)" id="applyFilter1">Apply filter</button>
    </div>

    <easy-data-table
        v-model:server-options="serverOptions"
        :server-items-length="serverItemsLength"
        :headers="headers"
        :items="servicePriceLogs"
        :search-field="searchField"
        :search-value="searchValue"
        :loading="loading"
        alternating
        border-cell
        buttons-pagination
    >

      <template #item-user_name="item">
        {{ item.user_name || 'N/A' }}
      </template>

      <template #item-garment_type_name="item">
        <router-link
            v-if="item.garment_type_id"
            target="_blank"
            :to="{ name: 'GarmentTypeEdit', params: { id: item.garment_type_id } }"
        >
          {{ item.garment_type_name || 'N/A' }}
        </router-link>
        <span v-else>{{ item.garment_type_name || 'N/A' }}</span>
      </template>

      <template #item-service_name="item">
        {{ item.service_name || 'N/A' }}
      </template>

      <template #item-details="item">
        <div class="details-cell">{{ item.details }}</div>
      </template>

      <template #item-price_change_direction="item">
        <div class="price-change" :class="item.price_change_direction">
          <template v-if="item.price_change_direction === 'increase'">
            <span aria-label="Increase" title="Increase">▲</span>
          </template>
          <template v-else-if="item.price_change_direction === 'decrease'">
            <span aria-label="Decrease" title="Decrease">▼</span>
          </template>
          <template v-else>
            N/A
          </template>
        </div>
      </template>

      <template #item-logged_at="item">
        {{ formatDateTimeEN(item.logged_at) }}
      </template>

      <template #empty-message>
        No service price logs found.
      </template>
    </easy-data-table>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { useRouter } from 'vue-router'
import LogsService from "@/services/LogsService";

const router = useRouter();
const getGarmentTypeId = () => {
  const routeGarmentTypeId = router.currentRoute.value.params.garmenttypeId;

  if (!routeGarmentTypeId) {
    return null;
  }

  const parsedId = parseInt(routeGarmentTypeId, 10);

  return Number.isNaN(parsedId) ? null : parsedId;
};

const garmentTypeId = ref(getGarmentTypeId());

const servicePriceLogs = ref([]);
const searchField = ref("action");
const searchValue = ref("");
const garmentTypeFilter = ref("");

const nowDate = new Date();
const currentDate = new Date(Date.UTC(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate()));
const defaultStartDate = new Date(Date.UTC(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate() - 30));

const selectedStartDate = ref(defaultStartDate);
const selectedEndDate = ref(currentDate);
const datepickerClearedKey = ref(0);

const headers = [
  { text: "User", value: "user_name", sortable: true },
  { text: "Garment Name", value: "garment_type_name", sortable: true },
  { text: "Service", value: "service_name", sortable: true },
  { text: "Details", value: "details", sortable: false },
  { text: "Increase/Decrease", value: "price_change_direction", sortable: true },
  { text: "Action Timestamp", value: "logged_at", sortable: true },
];

const loading = ref(false);
const serverItemsLength = ref(0);
const serverOptions = ref({
  page: 1,
  rowsPerPage: 25,
  sortBy: "logged_at",
  sortType: "desc",
  exportFormat: "none",
  start_date: defaultStartDate,
  end_date: currentDate,
});

const forceRenderDatepicker = () => {
  datepickerClearedKey.value += 1;
};

const formatDateTimeEN = (date) => {
  if (!date) {
    return "";
  }

  const options = { year: 'numeric', month: 'numeric', day: 'numeric' };
  const formattedDate = new Date(date).toLocaleDateString('en-GB', options);
  const formattedTime = new Date(date).toLocaleTimeString('en-GB');

  return formattedDate + ' ' + formattedTime;
};

const createDateAsUTC = (date) => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
};

const loadFromServer = async () => {
  loading.value = true;
  const {
    serverCurrentPageItems,
    serverTotalItemsLength,
  } = await LogsService.searchServicePriceLogs(garmentTypeId.value, serverOptions.value);

  servicePriceLogs.value = serverCurrentPageItems;
  serverItemsLength.value = serverTotalItemsLength;
  loading.value = false;
};

const applyFilter = (resetPage) => {
  const filters = {};
  const updateOptions = serverOptions.value;

  filters.garment_type_name = garmentTypeFilter.value;

  updateOptions.filters = filters;

  if (selectedStartDate.value !== "" && typeof selectedStartDate.value !== "undefined" && selectedStartDate.value !== null) {
    updateOptions.start_date = createDateAsUTC(new Date(selectedStartDate.value));
  }

  if (selectedEndDate.value !== "" && typeof selectedEndDate.value !== "undefined" && selectedEndDate.value !== null) {
    updateOptions.end_date = createDateAsUTC(new Date(selectedEndDate.value));
  }

  if (resetPage) {
    updateOptions.page = 1;
  }

  serverOptions.value = updateOptions;
  loadFromServer();
};

const clearFilter = () => {
  const filters = {};

  const updateServerOptions = serverOptions.value;
  updateServerOptions.filters = filters;
  updateServerOptions.page = 1;
  updateServerOptions.start_date = defaultStartDate;
  updateServerOptions.end_date = currentDate;
  selectedStartDate.value = defaultStartDate;
  selectedEndDate.value = currentDate;
  garmentTypeFilter.value = "";
  serverOptions.value = updateServerOptions;

  forceRenderDatepicker();
  loadFromServer();
};

loadFromServer();

watch(() => serverOptions.value.page, () => {
  applyFilter(false);
});

watch(() => serverOptions.value.rowsPerPage, () => {
  applyFilter(false);
});

watch(
  () => [serverOptions.value.sortBy, serverOptions.value.sortType],
  () => {
    applyFilter(false);
  },
  { deep: true }
);

watch(() => router.currentRoute.value.params.garmenttypeId, () => {
  garmentTypeId.value = getGarmentTypeId();
  serverOptions.value.page = 1;
  loadFromServer();
});
</script>

<style scoped>
.details-cell {
  white-space: normal;
}

.price-change {
  align-items: center;
  display: inline-flex;
  font-weight: 600;
  justify-content: center;
  min-width: 2rem;
}

.price-change.increase {
  color: #1b7f3b;
}

.price-change.decrease {
  color: #c0392b;
}
</style>